<!-- Floating Toggle Button -->
<button 
  type="button"
  class="chatbot-toggle-btn"
  [class.chatbot-toggle-btn-open]="isOpen"
  [class.chatbot-toggle-btn-pulse]="hasMessages && !isOpen"
  (click)="toggleChat()"
  [attr.aria-label]="isOpen ? 'Close AI Assistant' : 'Open AI Assistant'">
  <aca-icon [icon]="isOpen ? icons.close : icons.robot" class="chatbot-icon"></aca-icon>
</button>

<!-- Welcome Popup -->
@if (showWelcome && !isOpen) {
<div class="welcome-popup">
  <div class="welcome-header">
    <div class="welcome-title-row">
      <aca-icon [icon]="icons.robot" class="welcome-icon"></aca-icon>
      <span class="welcome-title">ACA AI Assistant</span>
    </div>
    <button type="button" class="welcome-close-btn" (click)="closeWelcome()" aria-label="Close">
      <aca-icon [icon]="icons.close" class="close-icon"></aca-icon>
    </button>
  </div>

  <p class="welcome-message">
    Hi! I'm your ACA AI Assistant. I'm here to help you review and optimize your deal details.
  </p>

  <div class="welcome-actions">
    <button type="button" class="btn-get-started" (click)="handleGetStarted()">
      Let's get started!
    </button>
    <button type="button" class="btn-later" (click)="closeWelcome()">
      Later
    </button>
  </div>
</div>
}

<!-- Chat Interface -->
@if (isOpen) {
<div class="chat-container">
  <!-- Header -->
  <div class="chat-header">
    <div class="header-content">
      <div class="header-icon-wrapper">
        <aca-icon [icon]="icons.robot" class="header-icon"></aca-icon>
      </div>
      <div class="header-text">
        <h2 class="header-title">ACA AI Assistant</h2>
        <p class="header-subtitle">AI-powered assistance</p>
      </div>
    </div>
    <button type="button" class="close-btn" (click)="closeChat()" aria-label="Close chat">
      <aca-icon [icon]="icons.close" class="close-icon"></aca-icon>
    </button>
  </div>

    <!-- Messages -->
    <div class="messages-container" #messagesContainer>
      <div class="messages-list">
        @for (message of messages; track message.id) {
        <div class="message-wrapper"
             [class.message-user]="message.type === 'user'"
             [class.message-bot]="message.type === 'bot'">

          @if (message.type === 'bot') {
          <div class="message-avatar bot-avatar">
            <aca-icon [icon]="icons.robot" class="avatar-icon"></aca-icon>
          </div>
          }

          <div class="message-content-wrapper">
            <div class="message-bubble"
                 [class.bubble-user]="message.type === 'user'"
                 [class.bubble-bot]="message.type === 'bot'">
              <p class="message-text">{{ message.content }}</p>
            </div>

            <!-- Deal Fields Preview -->
            @if (message.dealFields) {
            <div class="deal-fields-preview">
              <div class="preview-header">
                <aca-icon [icon]="icons.sparkles" class="preview-icon"></aca-icon>
                <span class="preview-title">Suggested Updates</span>
              </div>
              <div class="preview-fields">
                @for (field of message.dealFields; track field.field) {
                <div class="preview-field">
                  <span class="field-label">{{ field.label }}:</span>
                  <span class="field-value">\${{ field.value }}</span>
                </div>
                }
              </div>
              <button type="button" class="apply-btn" (click)="applyDealFields(message.dealFields)">
                Apply These Changes
              </button>
            </div>
            }

            <!-- Tour Trigger Button -->
            @if (message.tourTrigger) {
            <div class="tour-trigger-preview">
              <button type="button"
                      class="tour-start-btn" 
                      (click)="startTour(message.tourType)"
                      [disabled]="!tourContentService"
                      [title]="!tourContentService ? 'Tour only available on Deal Rehash detail pages' : 'Start the interactive tour'">
                 Start Interactive Tour
                @if (!tourContentService) {
                <span class="tour-disabled-hint">(Not available on this page)</span>
                }
              </button>
            </div>
            }

            <!-- Suggestions -->
            @if (message.suggestions) {
            <div class="suggestions-wrapper">
              @for (suggestion of message.suggestions; track suggestion) {
              <button type="button"
                      class="suggestion-btn"
                      (click)="handleSuggestionClick(suggestion)"
                      [attr.aria-label]="'Suggestion: ' + suggestion">
                {{ suggestion }}
              </button>
              }
            </div>
            }

            <p class="message-time">{{ message.timestamp | date:'shortTime' }}</p>
          </div>

          @if (message.type === 'user') {
          <div class="message-avatar user-avatar">
            <aca-icon [icon]="icons.user" class="avatar-icon"></aca-icon>
          </div>
          }
        </div>
        }

        <!-- Typing Indicator -->
        @if (isTyping) {
        <div class="message-wrapper message-bot">
          <div class="message-avatar bot-avatar">
            <aca-icon [icon]="icons.robot" class="avatar-icon"></aca-icon>
          </div>
          <div class="typing-indicator">
            <div class="typing-dot"></div>
            <div class="typing-dot"></div>
            <div class="typing-dot"></div>
          </div>
        </div>
        }
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="quick-actions">
      <button type="button" 
              class="quick-action-btn" 
              (click)="handleSuggestionClick('Lowest Fee Possible')"
              aria-label="Suggest lowest fee possible">
        <aca-icon [icon]="icons.dollar" class="action-icon"></aca-icon>
        Lowest Fee
      </button>
      <button type="button" 
              class="quick-action-btn" 
              (click)="handleSuggestionClick('Remove Stip for Trade')"
              aria-label="Remove stipulation for trade">
        <aca-icon [icon]="icons.car" class="action-icon"></aca-icon>
        Remove Stip
      </button>
      <button type="button" 
              class="quick-action-btn" 
              (click)="handleSuggestionClick('No Cash Down')"
              aria-label="No cash down payment option">
        <aca-icon [icon]="icons.creditCard" class="action-icon"></aca-icon>
        No Cash Down
      </button>
      <button type="button" 
              class="quick-action-btn" 
              (click)="handleSuggestionClick('Other cars that the customer budgets for')"
              aria-label="Show other cars that fit customer budget">
        <aca-icon [icon]="icons.box" class="action-icon"></aca-icon>
        Other Cars
      </button>
    </div>

    <!-- Input -->
    <div class="input-container">
      <input
        type="text"
        class="chat-input"
        [(ngModel)]="inputValue"
        (keypress)="handleKeyPress($event)"
        [disabled]="isTyping"
        placeholder="What optimization is needed for this deal?"
        #chatInput
        aria-label="Chat message input">
      <button 
        type="button"
        class="send-btn"
        (click)="handleSendMessage()"
        [disabled]="!inputValue.trim() || isTyping"
        aria-label="Send message">
        <aca-icon [icon]="icons.send" class="send-icon"></aca-icon>
      </button>
    </div>
</div>
}

